import React, { createContext, useState, useContext, ReactNode, useEffect, useMemo, useCallback } from 'react';
import GradeChangeInLeadingCriterioPrompt from '../components/Assessment/GradeChangeInLeadingCriterioPrompt.component';
import GradeChangePrompt from '../components/Assessment/GradeChangePrompt.component';
import {
  SchemaExamScore,
  ExamScoringSchema,
  ExamTask,
  ExamTaskGroupSolutionWithTasks,
  ExamTaskGroupSolutionScoreShelfResponse,
} from '../models/assessment.types';
import { CriteriaSetupResponseDto } from '../models/session.types';
import { Status } from '../services/exams/assessmentSession/assessments.types';
import { cleanPartialScores, setPartialScore, updateScoreNoAnswer } from '../services/scoring/scoring.service';
import { getCriterionFromTask, isCommentForScoreRequired } from './helpers';
import { useLayoutContext } from './Layout.context';
import _ from 'lodash';
import { TaskTypeFilterValue } from '../components/Assessment/TopMenu/TaskTypeDropdown.component';

export interface IScoringContext {
  examTaskGroupSolutionWithTasks: ExamTaskGroupSolutionWithTasks | null;
  criteriaSetups: CriteriaSetupResponseDto[] | null;
  activeScore: number | null;
  activeTask: ExamTask | null;
  activeCriterion: ExamScoringSchema | null;
  progress: number;
  maxTaskScore: number | undefined;
  totalTaskScore: number | null;
  totalAssessmentScore: number | null;
  totalScore: number | null;
  verifierTotalScore: number | null;
  maxTotalScore: number;
  isCommentForCurrentScoreRequired: boolean;
  isCommentForCurrentScoreProvided: boolean;
  activeCriterionDependentScores: SchemaExamScore[];
  orgzId?: string;
  examinerId: string;
  assessmentSessionId: string;
  processTypeId: string;
  deskId?: string;
  examinerFunction?: string;
  isTabView?: boolean;
  initialSheetId?: string;
  isAnyModalOpened: boolean;
  isSpecialCaseReported: boolean;
  gradingInfoModalOpen: boolean;
  isTrainingSession: boolean;
  taskTypeFilter: TaskTypeFilterValue;
  progressPromptOpen: boolean;
  sendbackPromptOpen: boolean;
  progressPromptInFooterOpen: boolean;
  activeCriterionIndex: number;
  gradeChangeErrorModalOpenForCriterion: ExamScoringSchema | null;
  isForcedCommentChanged: boolean;
  isCommentUpdate: boolean;
  isCommentEdit: boolean;
  hasScoreChangedSinceLastSendback: boolean;
  updateIsCommentForCurrentScoreProvided: (value: boolean) => void;
  loadTasks: (examTaskGroupSolutionWithTasks: ExamTaskGroupSolutionWithTasks) => void;
  setActiveTask: React.Dispatch<React.SetStateAction<ExamTask | null>>;
  activateCriterion: (criterion: ExamScoringSchema | null) => void;
  updateSendbackState: (examTaskSolutionStatus: Status) => void;
  updateExamTask: (examTask: ExamTask) => void;
  updateScore: (
    scoreCandidate: number | null,
    onBackendCall?: BackendCallback,
    onCancel?: () => void,
  ) => Promise<unknown>;
  openScoreShelf: (onBackendCall?: OpenShelfCallback) => Promise<unknown>;
  updateScoreIfValid: _.DebouncedFunc<
    (isValueValid: boolean, scoreCandidate: number | null, onBackendCall?: BackendCallback) => Promise<unknown>
  >;
  setNoAnswer: (value: boolean) => void;
  deleteTaskScore: () => void;
  getNextScore: () => number | null;
  getPrevScore: () => number | null;
  onBeforeScoreStore: (callback: BeforeScoreStoreCallback) => void;
  onUpdateScore: (callback: UpdateScoreCallback) => void;
  onDeleteTaskScore: (callback: DefaultCallback) => void;
  getSetupsForcingDependentCriterionReset: (
    leadingCriterion: ExamScoringSchema | null,
    newScore: number | null,
  ) => CriteriaSetupResponseDto[];
  getCriterionFromActiveTask: (schemaId: string) => ExamScoringSchema | null;
  updateScoreInCriterion: (newScore: number | null, criterion: ExamScoringSchema) => number | null;
  examTaskGroupSolutionScoreVersionId: string | undefined;
  setExamTaskGroupSolutionScoreVersionId: React.Dispatch<React.SetStateAction<string | undefined>>;
  updateIsSpecialCaseReported: (value: boolean) => void;
  setGradingInfoModalOpen: (value: boolean) => void;
  setIsTrainingSession: (value: boolean) => void;
  setTaskTypeFilter: (value: TaskTypeFilterValue) => void;
  setProgressPromptOpen: (value: boolean) => void;
  setSendbackPromptOpen: (value: boolean) => void;
  setProgressPromptInFooterOpen: (value: boolean) => void;
  setGradeChangeErrorModalOpenForCriterion: React.Dispatch<React.SetStateAction<ExamScoringSchema | null>>;
  setActiveCriterionIndex: React.Dispatch<React.SetStateAction<number>>;
  setIsForcedCommentChanged: (value: boolean) => void;
  setIsCommentUpdate: (value: boolean) => void;
  setIsCommentEdit: (value: boolean) => void;
}

type BackendCallback = (
  newScore: number | null,
  criterion: ExamScoringSchema,
  responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined,
) => Promise<unknown>;
type BeforeScoreStoreCallback = (
  criterion: ExamScoringSchema | null,
  task: ExamTask | null,
) => Promise<ExamTaskGroupSolutionScoreShelfResponse | undefined>;
type UpdateScoreCallback = (newScore: number | null, prevScore: number | null, criterion: ExamScoringSchema) => void;
type DefaultCallback = () => void;
type OpenShelfCallback = (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => Promise<unknown>;

interface ScoringProviderProps {
  orgzId?: string;
  examinerId: string;
  assessmentSessionId?: string;
  processTypeId?: string;
  deskId?: string;
  examinerFunction?: string;
  children: ReactNode;
  isTabView?: boolean;
  initialSheetId?: string;
}

const ScoringContext = createContext<IScoringContext | null>(null);

export const ScoringProvider: React.FC<ScoringProviderProps> = (props) => {
  const { openAlert, showLoader } = useLayoutContext();

  const [examTaskGroupSolutionWithTasks, setExamTaskGroupSolutionWithTasks] =
    useState<ExamTaskGroupSolutionWithTasks | null>(null);
  const [activeTask, setActiveTask] = useState<ExamTask | null>(null);
  const [activeCriterion, setActiveCriterion] = useState<ExamScoringSchema | null>(null);
  const [originalScore, setOriginalScore] = useState<number | null>(null);
  const [gradeChangeOpen, setGradeChangeOpen] = useState<boolean>(false);
  const [gradeChangeForLeadingCriterionOpen, setGradeChangeForLeadingCriterionOpen] = useState<boolean>(false);
  const [criterionsToBeResetNames, setCriterionsToBeResetNames] = useState<string>('');
  const [onGradeChangeConfirm, setOnGradeChangeConfirm] = useState<(() => void) | null>(null);
  const [onGradeChangeCancel, setOnGradeChangeCancel] = useState<(() => void) | null>(null);
  const [maxTotalScore, setMaxTotalScore] = useState<number>(0);
  const [totalScore, setTotalScore] = useState<number | null>(null);
  const [verifierTotalScore, setVerifierTotalScore] = useState<number | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [maxTaskScore, setMaxTaskScore] = useState<number | undefined>(undefined);
  const [totalTaskScore, setTotalTaskScore] = useState<number | null>(null);
  const [totalAssessmentScore, setTotalAssessmentScore] = useState<number | null>(null);
  const [updateScoreCallback, setUpdateScoreCallback] = useState<UpdateScoreCallback | null>(null);
  const [beforeScoreStoreCallback, setBeforeScoreStoreCallback] = useState<BeforeScoreStoreCallback | null>(null);
  const [deleteTaskScoreCallback, setDeleteTaskScoreCallback] = useState<DefaultCallback | null>(null);
  const [isCommentForCurrentScoreProvided, setIsCommentForCurrentScoreProvided] = useState<boolean>(false);
  const [examTaskGroupSolutionScoreVersionId, setExamTaskGroupSolutionScoreVersionId] = useState<string | undefined>(
    undefined,
  );
  const [onContinue, setOnContinue] = useState<
    ((criterion: ExamScoringSchema | null, task: ExamTask | null) => void) | null
  >(null);
  const [gradingInfoModalOpen, setGradingInfoModalOpen] = useState<boolean>(false);
  const [gradeChangeErrorModalOpenForCriterion, setGradeChangeErrorModalOpenForCriterion] =
    useState<ExamScoringSchema | null>(null);
  const [isTrainingSession, setIsTrainingSession] = useState<boolean>(false);
  const [taskTypeFilter, setTaskTypeFilter] = useState<TaskTypeFilterValue>('downloaded');
  const [progressPromptOpen, setProgressPromptOpen] = useState<boolean>(false);
  const [sendbackPromptOpen, setSendbackPromptOpen] = useState<boolean>(false);
  const [progressPromptInFooterOpen, setProgressPromptInFooterOpen] = useState<boolean>(false);
  const [activeCriterionIndex, setActiveCriterionIndex] = useState<number>(-1);
  const [isCommentUpdate, setIsCommentUpdate] = useState<boolean>(false);
  const [isForcedCommentChanged, setIsForcedCommentChanged] = useState<boolean>(false);
  const [isCommentEdit, setIsCommentEdit] = useState<boolean>(false);
  const [hasScoreChangedSinceLastSendback, setHasScoreChangedSinceLastSendback] = useState<boolean>(false);

  const isAnyModalOpened =
    gradeChangeOpen ||
    gradeChangeForLeadingCriterionOpen ||
    gradingInfoModalOpen ||
    progressPromptOpen ||
    progressPromptInFooterOpen ||
    !!gradeChangeErrorModalOpenForCriterion;

  useEffect(() => {
    if (onContinue) {
      onContinue(activeCriterion, activeTask);
      setOnContinue(null);
    }
  }, [onContinue, examTaskGroupSolutionWithTasks, activeCriterion, activeTask]);

  useEffect(() => {
    if (!gradeChangeForLeadingCriterionOpen) {
      setCriterionsToBeResetNames('');
    }
  }, [gradeChangeForLeadingCriterionOpen]);

  const getCriterionFromActiveTask = useCallback(
    (schemaId: string): ExamScoringSchema | null => {
      if (!activeTask) {
        return null;
      }
      return getCriterionFromTask(schemaId, activeTask);
    },
    [activeTask],
  );

  const getCriterionLeadingSetups = useCallback(
    (criterion: ExamScoringSchema): CriteriaSetupResponseDto[] => {
      if (!examTaskGroupSolutionWithTasks?.criteriaSetups) {
        return [];
      }
      return examTaskGroupSolutionWithTasks.criteriaSetups.filter(
        (setup) => setup.leadingSchemaId === criterion.examScoringSchemaId,
      );
    },
    [examTaskGroupSolutionWithTasks?.criteriaSetups],
  );

  const getSetupsForcingDependentCriterionReset = (
    leadingCriterion: ExamScoringSchema | null,
    newScore: number | null,
  ): CriteriaSetupResponseDto[] => {
    if (!leadingCriterion) {
      return [];
    }
    const allActiveCriterionLeadingSetups = getCriterionLeadingSetups(leadingCriterion);
    return allActiveCriterionLeadingSetups.filter((setup) => {
      const dependentCriterion = getCriterionFromActiveTask(setup.dependentSchemaId);
      return (
        dependentCriterion &&
        dependentCriterion.score !== null &&
        (newScore === null ||
          (newScore >= setup.scoresForLeadingSchema[0].score &&
            newScore <= setup.scoresForLeadingSchema[1].score &&
            (dependentCriterion.score < setup.scoresForDependentSchema[0].score ||
              dependentCriterion.score > setup.scoresForDependentSchema[1].score)))
      );
    });
  };

  const activeCriterionDependentScores = useMemo(() => {
    if (examTaskGroupSolutionWithTasks && examTaskGroupSolutionWithTasks.criteriaSetups && activeCriterion) {
      const setupsWithDependentActiveCriterion = examTaskGroupSolutionWithTasks.criteriaSetups.filter(
        (setup) => setup.dependentSchemaId === activeCriterion.examScoringSchemaId,
      );

      const setupsLimitingScores: CriteriaSetupResponseDto[] = setupsWithDependentActiveCriterion.filter((setup) => {
        const leadingSchema = getCriterionFromActiveTask(setup.leadingSchemaId);
        return (
          leadingSchema &&
          leadingSchema.score !== null &&
          leadingSchema.score >= setup.scoresForLeadingSchema[0].score &&
          leadingSchema.score <= setup.scoresForLeadingSchema[1].score
        );
      });

      if (setupsLimitingScores.length) {
        const minScore = Math.min(...setupsLimitingScores.map((setup) => setup.scoresForDependentSchema[0].score));
        const maxScore = Math.max(...setupsLimitingScores.map((setup) => setup.scoresForDependentSchema[1].score));

        const result = activeCriterion.examScoreValues.filter(
          (scoreItem) => scoreItem.score >= minScore && scoreItem.score <= maxScore,
        );
        return result;
      } else {
        return activeCriterion.examScoreValues;
      }
    } else {
      return activeCriterion ? activeCriterion.examScoreValues : [];
    }
  }, [activeCriterion, examTaskGroupSolutionWithTasks?.criteriaSetups, getCriterionFromActiveTask]);

  const getCurrentScoreIndex = (): number => {
    return !activeCriterion || activeCriterion.score === null
      ? -1
      : activeCriterionDependentScores.map((e) => e.score).findIndex((item) => item === activeCriterion.score);
  };

  const getNextStoreSafe = (scores: SchemaExamScore[], index: number): number | null => {
    if (scores.length > index) return scores[index].score;
    return null;
  };

  const getNextScore = (): number | null => {
    if (!activeCriterion) return null;
    const nextScoreIndex = getCurrentScoreIndex() + 1;
    if (nextScoreIndex <= activeCriterionDependentScores.length - 1) {
      return activeCriterionDependentScores[nextScoreIndex]?.score &&
        activeCriterionDependentScores[nextScoreIndex].score > 0
        ? activeCriterionDependentScores[nextScoreIndex].score
        : getNextStoreSafe(activeCriterionDependentScores, nextScoreIndex + 1);
    } else {
      return null;
    }
  };

  const getPrevScore = (): number | null => {
    if (!activeCriterion) return null;

    const prevScoreIndex = getCurrentScoreIndex() - 1;
    return prevScoreIndex >= 0 && activeCriterionDependentScores[prevScoreIndex].score > 0
      ? activeCriterionDependentScores[prevScoreIndex].score
      : null;
  };

  const updateMaxTotalScore = () => {
    if (!examTaskGroupSolutionWithTasks) return;
    let score = 0;
    examTaskGroupSolutionWithTasks.examTasksCriteria.forEach((examTask: ExamTask) =>
      examTask.examScoringSchemas.forEach((schema) => {
        if (typeof schema.examScoreValues[0] === 'number') {
          score = score + schema.examScoreValues[0];
        } else {
          score = score + Math.max(...schema.examScoreValues.map((e) => e.score));
        }
      }),
    );
    setMaxTotalScore(score);
  };

  const updateTotalScore = () => {
    if (!examTaskGroupSolutionWithTasks) return;
    let score: number | null = null;
    examTaskGroupSolutionWithTasks.examTasksCriteria.forEach((examTask: ExamTask) =>
      examTask.examScoringSchemas.map((schema) => {
        if (schema.score !== null) {
          if (score === null) {
            score = schema.score;
          } else {
            score = score + schema.score;
          }
        } else return;
      }),
    );
    setTotalScore(score);
  };

  const updateVerifierTotalScore = () => {
    if (!examTaskGroupSolutionWithTasks) return;
    let score: number | null = null;
    examTaskGroupSolutionWithTasks.examTasksCriteria.forEach((examTask: ExamTask) =>
      examTask.examScoringSchemas.map((schema) => {
        if (schema.verifierScore !== null) {
          if (score === null) {
            score = schema.verifierScore;
          } else {
            score = score + schema.verifierScore;
          }
        }
      }),
    );
    setVerifierTotalScore(score);
  };

  const updateProgress = () => {
    if (!examTaskGroupSolutionWithTasks) return;

    let count = 0;
    let scoringSchemasCount = 0;
    examTaskGroupSolutionWithTasks.examTasksCriteria.forEach((examTask: ExamTask) => {
      examTask.examScoringSchemas.forEach((schema: ExamScoringSchema) => {
        scoringSchemasCount++;
        if (examTask.noAnswer || schema.score !== null) {
          count++;
        }
      });
    });
    const progress = Math.round((count / scoringSchemasCount) * 100);
    setProgress(progress);
  };

  const updateTaskTotalScore = () => {
    if (!activeTask) return;

    let maxScore = 0;
    let nonEmptyScores = false;
    let hasSampleScore = false;

    const score = activeTask.examScoringSchemas.reduce((sum: number, nextSchema: ExamScoringSchema) => {
      if (typeof nextSchema.examScoreValues[0] === 'number') {
        maxScore = maxScore + nextSchema.examScoreValues[0];
      } else maxScore = maxScore + Math.max(...nextSchema.examScoreValues.map((e) => e.score));
      if (nextSchema.score !== null) nonEmptyScores = true;
      return sum + (nextSchema.score ? nextSchema.score : 0);
    }, 0);

    const sampleScoreSum = activeTask.examScoringSchemas.reduce((sum: number, scoringSchema: ExamScoringSchema) => {
      if (scoringSchema.sampleScore !== null) hasSampleScore = true;
      return sum + (scoringSchema.sampleScore !== null ? scoringSchema.sampleScore : 0);
    }, 0);

    if (activeTask.noAnswer) setTotalTaskScore(0);
    else if (nonEmptyScores) setTotalTaskScore(score);
    else setTotalTaskScore(null);
    setMaxTaskScore(maxScore);
    setTotalAssessmentScore(hasSampleScore ? sampleScoreSum : null);
  };

  const updateStatistics = () => {
    updateProgress();
    updateTaskTotalScore();
    updateTotalScore();
    updateVerifierTotalScore();
    updateMaxTotalScore();
  };

  const onBeforeScoreStore = (callback: BeforeScoreStoreCallback) => {
    setBeforeScoreStoreCallback(() => callback);
  };

  const onUpdateScore = (callback: UpdateScoreCallback) => {
    setUpdateScoreCallback(() => callback);
  };

  const onDeleteTaskScore = (callback: DefaultCallback) => {
    setDeleteTaskScoreCallback(() => callback);
  };

  const updateScoreInCriterion = (newScore: number | null, criterion: ExamScoringSchema): number | null => {
    let prevScore: number | null = null;
    const newValue = (prevState: ExamTaskGroupSolutionWithTasks | null) => {
      return prevState
        ? {
            ...prevState,
            examTasksCriteria: prevState.examTasksCriteria.map((item: ExamTask) => {
              return item.taskId === activeTask?.taskId
                ? {
                    ...item,
                    examScoringSchemas: item.examScoringSchemas.map((scheme) => {
                      if (scheme.examScoringSchemaId === criterion.examScoringSchemaId) {
                        prevScore = scheme.score;
                        return { ...scheme, score: newScore };
                      } else {
                        return scheme;
                      }
                    }),
                  }
                : item;
            }),
          }
        : prevState;
    };
    setExamTaskGroupSolutionWithTasks(newValue);
    return prevScore;
  };

  const updateActiveScore = (newScore: number | null, criterion: ExamScoringSchema) => {
    const prevScore = updateScoreInCriterion(newScore, criterion);
    setOriginalScore(null);
    setHasScoreChangedSinceLastSendback(true);
    updateScoreCallback && updateScoreCallback(newScore, prevScore, criterion);
  };

  const storeScore = (newScore: number | null, criterion: ExamScoringSchema): Promise<unknown> => {
    const isNewScoreSameAsPrev = newScore === criterion.score;
    if (isNewScoreSameAsPrev) return Promise.resolve();

    return new Promise((resolve, reject) => {
      if (criterion) {
        showLoader(true, 'Aktualizacja oceny');
        setPartialScore(criterion.partialScoreId, newScore, props.examinerId)
          .then(() => {
            resolve({});
          })
          .finally(() => showLoader(false));
      } else {
        return reject();
      }
    });
  };

  const resetScore = () => {
    setOriginalScore(activeCriterion ? activeCriterion.score : null);
    updateStatistics();
  };

  const updateScore = (scoreCandidate: number | null, onBackendCall?: BackendCallback, onCancel?: () => void) => {
    return new Promise<ExamTaskGroupSolutionScoreShelfResponse | undefined>((resolve) => {
      const asyncBackendCallback = async (
        criterion: ExamScoringSchema,
        responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined,
      ) => {
        const backendCall = onBackendCall ?? storeScore;
        if (backendCall) {
          backendCall(scoreCandidate, criterion, responseData).then(() => {
            updateActiveScore(scoreCandidate, criterion);
            resolve(responseData);
          });
        }
      };

      const setupsForcingDependentCriterionReset = getSetupsForcingDependentCriterionReset(
        activeCriterion,
        scoreCandidate,
      );

      if ((originalScore !== null && scoreCandidate !== originalScore) || setupsForcingDependentCriterionReset.length) {
        if (setupsForcingDependentCriterionReset.length) {
          setGradeChangeForLeadingCriterionOpen(true);
          setCriterionsToBeResetNames(
            setupsForcingDependentCriterionReset
              .map((setup) => {
                const dependCriterion = getCriterionFromActiveTask(setup.dependentSchemaId);
                if (dependCriterion) {
                  return `${dependCriterion.label} ${dependCriterion.criterionName}`;
                } else {
                  return undefined;
                }
              })
              .filter((item) => item)
              .join(', '),
          );
        } else {
          if (
            activeCriterion &&
            [Status.Open, Status.New].includes(
              examTaskGroupSolutionWithTasks?.examTaskGroupSolutionScoreState ?? Status.Closed,
            )
          ) {
            asyncBackendCallback(activeCriterion, undefined);
          } else {
            setGradeChangeOpen(true);
          }
        }
        setOnGradeChangeConfirm(() => () => {
          const continueCallback = (
            criterion: ExamScoringSchema | null,
            responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined,
          ) => {
            criterion && asyncBackendCallback(criterion, responseData);
            setOnGradeChangeConfirm(null);
          };
          setGradeChangeOpen(false);
          setGradeChangeForLeadingCriterionOpen(false);
          if (beforeScoreStoreCallback) {
            beforeScoreStoreCallback(activeCriterion, null).then(
              (responseData) =>
                setOnContinue(() => (criterion: ExamScoringSchema | null) => continueCallback(criterion, responseData)),
              () => openAlert('send_error'),
            );
          } else if (activeCriterion) {
            continueCallback(activeCriterion, undefined);
          }
        });
        setOnGradeChangeCancel(() => () => {
          onCancel?.();
          resetScore();
          setOnGradeChangeCancel(null);
          resolve(undefined);
        });
      } else if (activeCriterion) {
        asyncBackendCallback(activeCriterion, undefined);
      }
    });
  };

  const openScoreShelf = (onBackendCall?: OpenShelfCallback) => {
    return new Promise<ExamTaskGroupSolutionScoreShelfResponse | undefined>(() => {
      if (activeCriterion) {
        if (beforeScoreStoreCallback && onBackendCall) {
          beforeScoreStoreCallback(activeCriterion, null).then(
            (responseData) => {
              setOnContinue(() => (criterion: ExamScoringSchema | null) => onBackendCall(responseData));
            },
            () => openAlert('send_error'),
          );
        }
      }
    });
  };

  const updateScoreIfValid = _.debounce(
    (isValueValid: boolean, scoreCandidate: number | null, onBackendCall?: BackendCallback) => {
      if (isValueValid) {
        return updateScore(scoreCandidate, onBackendCall);
      }
    },
    500,
  );

  const updateExamTask = (examTask: ExamTask) => {
    if (!examTaskGroupSolutionWithTasks) {
      throw new Error('too early');
    }
    setExamTaskGroupSolutionWithTasks((prevState) => {
      return prevState
        ? {
            ...prevState,
            examTasksCriteria: prevState.examTasksCriteria.map((item: ExamTask) => {
              return item.taskId === examTask.taskId ? examTask : item;
            }),
          }
        : prevState;
    });
  };

  const runDeleteTaskScoreAction = (task: ExamTask | null) => {
    task &&
      cleanPartialScores(task.solutionScoreId)
        .then(
          () => {
            const updatedExamScoringSchemas = task.examScoringSchemas.map((scoringSchema: ExamScoringSchema) => {
              const updatedScoringSchema = { ...scoringSchema };
              updatedScoringSchema.score = null;
              return updatedScoringSchema;
            });
            deleteTaskScoreCallback && deleteTaskScoreCallback();
            updateExamTask({ ...task, examScoringSchemas: updatedExamScoringSchemas });
          },
          () => openAlert('delete_error'),
        )
        .finally(() => showLoader(false));
  };

  const deleteTaskScore = () => {
    if (!activeTask) return;
    showLoader(true, 'Aktualizacja oceny');
    if (beforeScoreStoreCallback) {
      beforeScoreStoreCallback(null, activeTask).then(
        () =>
          activeTask &&
          setOnContinue(
            () => (criterion: ExamScoringSchema | null, task: ExamTask | null) => runDeleteTaskScoreAction(task),
          ),
        () => openAlert('send_error'),
      );
    } else {
      runDeleteTaskScoreAction(activeTask);
    }
  };

  const handleGradeChangeClose = () => {
    setGradeChangeOpen(false);
    setGradeChangeForLeadingCriterionOpen(false);
    if (onGradeChangeCancel) {
      onGradeChangeCancel();
    }
  };

  const handleGradeChangeConfirm = () => {
    if (onGradeChangeConfirm) {
      onGradeChangeConfirm();
    }
  };

  const loadTasks = (examTaskGroupSolutionWithTasks: ExamTaskGroupSolutionWithTasks) => {
    setExamTaskGroupSolutionWithTasks(examTaskGroupSolutionWithTasks);
    setHasScoreChangedSinceLastSendback(false);
  };

  const updateSendbackState = (examTaskSolutionStatus: Status) => {
    if (!examTaskGroupSolutionWithTasks) return;
    setExamTaskGroupSolutionWithTasks((prevState: ExamTaskGroupSolutionWithTasks | null) => {
      return prevState
        ? {
            ...prevState,
            examTaskGroupSolutionScoreState: examTaskSolutionStatus,
          }
        : prevState;
    });
    setHasScoreChangedSinceLastSendback(false);
    resetScore();
  };

  const runSetNoAnswerAction = (task: ExamTask | null, value: boolean) => {
    showLoader(true, 'Aktualizacja oceny');
    task &&
      updateScoreNoAnswer(task.solutionScoreId, value, props.examinerId).then(
        () => {
          updateExamTask({
            ...task,
            noAnswer: value,
            examScoringSchemas: task.examScoringSchemas.map((scoringSchema: ExamScoringSchema) => {
              return { ...scoringSchema, score: value ? 0 : null };
            }),
          });
          if (value) {
            deleteTaskScoreCallback?.();
          }
          showLoader(false);
        },
        () => {
          showLoader(false);
          openAlert('send_error');
        },
      );
  };

  const setNoAnswer = (value: boolean) => {
    if (!activeTask) return;

    if (beforeScoreStoreCallback) {
      beforeScoreStoreCallback(null, activeTask).then(
        () =>
          activeTask &&
          setOnContinue(
            () => (criterion: ExamScoringSchema | null, task: ExamTask | null) => runSetNoAnswerAction(task, value),
          ),
        () => openAlert('save_error'),
      );
    } else {
      runSetNoAnswerAction(activeTask, value);
    }
  };

  const activeScore: number | null = activeCriterion ? activeCriterion.score : null;

  useEffect(() => {
    if (!examTaskGroupSolutionScoreVersionId) {
      resetScore();
    }
  }, [examTaskGroupSolutionScoreVersionId]);

  const activateCriterion = (criterion: ExamScoringSchema | null) => {
    setActiveCriterion(criterion);
    if (criterion) {
      resetScore();
    } else {
      setActiveCriterionIndex(-1);
    }
  };

  useEffect(() => {
    if (activeTask && examTaskGroupSolutionWithTasks) {
      const newActiveTask = examTaskGroupSolutionWithTasks.examTasksCriteria.find(
        (item) => item.taskId === activeTask.taskId,
      );
      if (newActiveTask) {
        setActiveTask(newActiveTask);

        if (activeCriterion) {
          const newActiveCriterion = newActiveTask.examScoringSchemas.find(
            (item) => item.examScoringSchemaId === activeCriterion.examScoringSchemaId,
          );
          if (newActiveCriterion) {
            activateCriterion(newActiveCriterion);
          }
        }
      }
    }
  }, [examTaskGroupSolutionWithTasks, activeTask, activeCriterion]);

  useEffect(() => {
    updateStatistics();
  }, [examTaskGroupSolutionWithTasks, activeTask]);

  const isCommentForCurrentScoreRequired = isCommentForScoreRequired(activeScore, activeCriterion);

  const updateIsCommentForCurrentScoreProvided = (value: boolean) => {
    setIsCommentForCurrentScoreProvided(value);
  };

  const updateIsSpecialCaseReported = (value: boolean) => {
    setExamTaskGroupSolutionWithTasks((prevState) => {
      return prevState
        ? {
            ...prevState,
            isSpecialCaseReported: value,
          }
        : prevState;
    });
  };

  return (
    <ScoringContext.Provider
      value={{
        examTaskGroupSolutionWithTasks,
        criteriaSetups: examTaskGroupSolutionWithTasks?.criteriaSetups || null,
        activeScore,
        activeTask,
        activeCriterion,
        progress,
        maxTaskScore,
        totalTaskScore,
        totalAssessmentScore,
        totalScore,
        verifierTotalScore,
        maxTotalScore,
        isCommentForCurrentScoreRequired,
        isCommentForCurrentScoreProvided,
        activeCriterionDependentScores,
        orgzId: props.orgzId,
        examinerId: props.examinerId,
        assessmentSessionId: props.assessmentSessionId || '',
        processTypeId: props.processTypeId || '',
        deskId: props.deskId,
        examinerFunction: props.examinerFunction,
        isTabView: props.isTabView,
        initialSheetId: props.initialSheetId,
        isAnyModalOpened,
        isSpecialCaseReported: examTaskGroupSolutionWithTasks?.isSpecialCaseReported ?? false,
        gradingInfoModalOpen,
        isTrainingSession,
        taskTypeFilter,
        progressPromptOpen,
        sendbackPromptOpen,
        progressPromptInFooterOpen,
        activeCriterionIndex,
        gradeChangeErrorModalOpenForCriterion,
        isForcedCommentChanged,
        isCommentUpdate,
        isCommentEdit,
        hasScoreChangedSinceLastSendback,
        updateIsCommentForCurrentScoreProvided,
        loadTasks,
        setActiveTask,
        updateSendbackState,
        activateCriterion,
        updateExamTask,
        updateScore,
        openScoreShelf,
        updateScoreIfValid,
        setNoAnswer,
        deleteTaskScore,
        getNextScore,
        getPrevScore,
        onUpdateScore,
        onBeforeScoreStore,
        onDeleteTaskScore,
        getSetupsForcingDependentCriterionReset,
        getCriterionFromActiveTask,
        updateScoreInCriterion,
        examTaskGroupSolutionScoreVersionId,
        setExamTaskGroupSolutionScoreVersionId,
        updateIsSpecialCaseReported,
        setGradingInfoModalOpen,
        setIsTrainingSession,
        setTaskTypeFilter,
        setProgressPromptOpen,
        setSendbackPromptOpen,
        setProgressPromptInFooterOpen,
        setActiveCriterionIndex,
        setGradeChangeErrorModalOpenForCriterion,
        setIsForcedCommentChanged,
        setIsCommentUpdate,
        setIsCommentEdit,
      }}
    >
      {props.children}

      {gradeChangeOpen && activeTask && (
        <GradeChangePrompt
          open={gradeChangeOpen}
          onCancel={handleGradeChangeClose}
          onConfirm={handleGradeChangeConfirm}
          activeTask={activeTask}
          activeCriterion={activeCriterion}
        />
      )}

      {gradeChangeForLeadingCriterionOpen && (
        <GradeChangeInLeadingCriterioPrompt
          dependentCriterionsCaptions={criterionsToBeResetNames}
          onCancel={handleGradeChangeClose}
          onConfirm={() => {
            handleGradeChangeConfirm();
          }}
        />
      )}
    </ScoringContext.Provider>
  );
};

export const useScoring = () => {
  const contextData = useContext(ScoringContext);
  if (!contextData) throw new Error('no <ScoringProvider> wrapper');
  return contextData;
};
